/**
 * DeclineConnectionPage
 *
 * Confirmation page shown after declining a connection request.
 * Route: /connections/decline/:token
 *
 * Can also be used to directly decline a request via token.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  UserX,
  Clock,
  AlertCircle,
  Loader2,
  Home,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { connectionsApi } from '@/services/api/connections';
import {
  FOREST_GREEN,
  ERROR,
  WARNING,
  BACKGROUND,
  MID_GRAY,
  SHADOWS,
} from '@/styles/tokens';
import { TerraMatrixBrand } from '@/components/branding/TerraMatrixBrand';
import type { ConnectionRequestDetails } from '@/types/connections';
import * as errorUtils from '@/utils/errors';

type PageState =
  | 'loading'
  | 'confirm'
  | 'success'
  | 'already_declined'
  | 'already_accepted'
  | 'expired'
  | 'invalid'
  | 'processing'
  | 'error';

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * DeclineConnectionPage Component
 */
export const DeclineConnectionPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if we're coming from a successful decline
  const isSuccess = searchParams.get('success') === 'true';

  const [pageState, setPageState] = useState<PageState>(
    isSuccess ? 'success' : 'loading'
  );
  const [requestDetails, setRequestDetails] = useState<ConnectionRequestDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Validate the token
  const validateToken = useCallback(async () => {
    if (!token) {
      console.log('[DeclineConnectionPage] No token provided');
      setPageState('invalid');
      setErrorMessage('No connection request token provided.');
      return;
    }

    // If already showing success, don't validate
    if (isSuccess) {
      console.log('[DeclineConnectionPage] Showing success state');
      return;
    }

    console.log('[DeclineConnectionPage] Validating token:', token.substring(0, 8) + '...');

    try {
      const response = await connectionsApi.getRequestByToken(token);
      const details = response.data;

      if (details) {
        setRequestDetails(details);
        console.log('[DeclineConnectionPage] Token status:', details.status);

        if (details.status === 'already_declined') {
          setPageState('already_declined');
        } else if (details.status === 'already_accepted') {
          setPageState('already_accepted');
        } else {
          setPageState('confirm');
        }
      } else {
        setPageState('invalid');
        setErrorMessage('Invalid connection request link.');
      }
    } catch (error: unknown) {
      console.error('[DeclineConnectionPage] Validation error:', error);

      const status = errorUtils.getErrorStatus(error);
      const message = errorUtils.getApiErrorMessage(error, '');

      if (status === 410) {
        setPageState('expired');
        setErrorMessage(message || 'This connection request has expired.');
      } else if (status === 404) {
        setPageState('invalid');
        setErrorMessage(message || 'This connection request link is invalid or has already been used.');
      } else {
        setPageState('error');
        setErrorMessage(message || 'Failed to validate connection request. Please try again.');
      }
    }
  }, [token, isSuccess]);

  // Validate on mount
  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // Handle decline
  const handleDecline = async () => {
    if (!token) return;

    setPageState('processing');
    console.log('[DeclineConnectionPage] Declining connection request...');

    try {
      await connectionsApi.declineByToken(token);
      setPageState('success');

      toast.success('Connection request declined');
    } catch (error: unknown) {
      console.error('[DeclineConnectionPage] Decline error:', error);
      setPageState('error');
      setErrorMessage(
        errorUtils.getApiErrorMessage(
          error,
          'Failed to decline connection request. Please try again.',
        )
      );

      toast.error('Failed to decline connection', {
        description: errorUtils.getApiErrorMessage(error, 'Please try again.'),
      });
    }
  };

  // Get requester display name
  const getRequesterDisplayName = () => {
    if (!requestDetails?.requester) return 'Unknown User';
    const { firstName, lastName, username } = requestDetails.requester;
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    return username;
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-8"
      style={{ backgroundColor: BACKGROUND.secondary }}
    >
      {/* Header with logo */}
      <div className="mb-8">
        <Link to="/">
          <TerraMatrixBrand
            markClassName="h-10 w-10"
            nameClassName="text-3xl"
          />
        </Link>
      </div>

      {/* Card container */}
      <div
        className="w-full max-w-md rounded-xl overflow-hidden"
        style={{
          backgroundColor: BACKGROUND.primary,
          boxShadow: SHADOWS.xl,
        }}
      >
        {/* Loading State */}
        {pageState === 'loading' && (
          <div className="p-8 text-center">
            <Loader2
              className="h-12 w-12 animate-spin mx-auto mb-4"
              style={{ color: FOREST_GREEN.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Loading...</h2>
            <p className="text-muted-foreground">
              Please wait while we load the request details...
            </p>
          </div>
        )}

        {/* Confirm Decline State */}
        {pageState === 'confirm' && requestDetails?.requester && (
          <div className="p-8 text-center">
            {/* Requester avatar */}
            <Avatar className="h-16 w-16 mx-auto mb-4">
              <AvatarImage
                src={requestDetails.requester.avatar}
                alt={getRequesterDisplayName()}
              />
              <AvatarFallback
                style={{
                  backgroundColor: FOREST_GREEN.faint,
                  color: FOREST_GREEN.DEFAULT,
                }}
                className="text-lg font-medium"
              >
                {getInitials(
                  requestDetails.requester.firstName,
                  requestDetails.requester.lastName,
                  requestDetails.requester.username
                )}
              </AvatarFallback>
            </Avatar>

            <h2 className="text-xl font-semibold mb-2">Decline Connection?</h2>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to decline the connection request from{' '}
              <strong>{getRequesterDisplayName()}</strong>?
            </p>

            {/* Actions */}
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate(`/connections/accept/${token}`)}
              >
                Go Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleDecline}
              >
                <UserX className="h-4 w-4 mr-2" />
                Decline Request
              </Button>
            </div>
          </div>
        )}

        {/* Processing State */}
        {pageState === 'processing' && (
          <div className="p-8 text-center">
            <Loader2
              className="h-12 w-12 animate-spin mx-auto mb-4"
              style={{ color: FOREST_GREEN.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Processing...</h2>
            <p className="text-muted-foreground">
              Please wait...
            </p>
          </div>
        )}

        {/* Success State */}
        {pageState === 'success' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: MID_GRAY.lighter }}
            >
              <Check className="h-8 w-8" style={{ color: MID_GRAY.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Request Declined</h2>
            <p className="text-muted-foreground mb-6">
              You have declined the connection request.
              {requestDetails?.requester && (
                <> {getRequesterDisplayName()} has been notified.</>
              )}
            </p>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              <Home className="h-4 w-4 mr-2" />
              Go to Dashboard
            </Button>
          </div>
        )}

        {/* Already Declined State */}
        {pageState === 'already_declined' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: MID_GRAY.lighter }}
            >
              <UserX className="h-8 w-8" style={{ color: MID_GRAY.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Already Declined</h2>
            <p className="text-muted-foreground mb-6">
              This connection request has already been declined.
            </p>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              <Home className="h-4 w-4 mr-2" />
              Go to Dashboard
            </Button>
          </div>
        )}

        {/* Already Accepted State */}
        {pageState === 'already_accepted' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: FOREST_GREEN.faint }}
            >
              <Check className="h-8 w-8" style={{ color: FOREST_GREEN.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Already Connected</h2>
            <p className="text-muted-foreground mb-6">
              You have already accepted this connection request.
            </p>
            <Button
              onClick={() => navigate('/dashboard/connections')}
              style={{
                backgroundColor: FOREST_GREEN.DEFAULT,
                color: 'white',
              }}
            >
              View Connections
            </Button>
          </div>
        )}

        {/* Expired State */}
        {pageState === 'expired' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: WARNING.bg }}
            >
              <Clock className="h-8 w-8" style={{ color: WARNING.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Request Expired</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage || 'This connection request has expired.'}
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>
              <Home className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </div>
        )}

        {/* Invalid State */}
        {pageState === 'invalid' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: ERROR.bg }}
            >
              <AlertCircle className="h-8 w-8" style={{ color: ERROR.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invalid Request</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage ||
                'This connection request link is invalid or has already been used.'}
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>
              <Home className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </div>
        )}

        {/* Error State */}
        {pageState === 'error' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: ERROR.bg }}
            >
              <AlertCircle className="h-8 w-8" style={{ color: ERROR.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Something Went Wrong</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage || 'An error occurred while processing your request.'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/')}>
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
              <Button onClick={() => validateToken()}>Try Again</Button>
            </div>
          </div>
        )}
      </div>

      {/* Footer text */}
      <p className="mt-8 text-sm text-muted-foreground text-center">
        If you change your mind, they can always send another request.
      </p>
    </div>
  );
};

export default DeclineConnectionPage;
